/**
 * Color utilities for graph node styling.
 *
 * Node base colors are explicit from node/type definitions.
 * Search highlighting uses a Trimble amber gradient.
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * Math.max(0, Math.min(1, t)));
}

const AMBER_RGB = hexToRgb('#fbad26');
const GENERIC_FALLBACK_COLOR = '#8c95a3';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Similarity-score highlight colour.
 * score=1 → full Trimble amber (#fbad26) — highest similarity / focus node.
 * score=0 → very light amber — lowest match in results.
 * Non-matching nodes should be dimmed via opacity separately.
 */
export function scoreToHighlightColor(score: number): string {
  const t = (1 - Math.max(0, Math.min(1, score))) * 0.80; // blend up to 80% toward white
  const [r0, g0, b0] = AMBER_RGB;
  return `rgb(${lerp(r0, 255, t)},${lerp(g0, 255, t)},${lerp(b0, 255, t)})`;
}

/**
 * Base node colour from explicit node/type definition.
 * Falls back only when color is missing.
 */
export function getNodeBaseColor(nodeColor: string | undefined): string {
  const color = (nodeColor ?? '').trim();
  if (color === '') {
    return GENERIC_FALLBACK_COLOR;
  }
  return color;
}
